import { React, useEffect, useState, useContext } from "react";
import axios from "axios";
import { GlobalLoading, showLoading } from "react-global-loading";
import { MyContextProvider } from "../../../App";
import { API_URL } from "../../../Constant";
import { changeDateFormat, validateASIReportsRole } from "../../../Custom/Custom";
import { jwtDecode } from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import messages from "../../../assets/Messages.json";
import asiTitle from "../../../asi.json";
import { generateExcelASI } from "../../../Custom/ASIReport/PendencyReport/ExportaPendencyReportExcel";
import { GetASIRejectedDSLReportPDF } from "../../../Custom/ASIReport/PendencyReport/ExportaPendencyReportPdf";

function GetASIRejectedDSLReport() {
  const currentDate = new Date();
  const prevMonthDate = new Date(currentDate);
  prevMonthDate.setMonth(currentDate.getMonth() - 1);
  const prevMonthName = prevMonthDate
    .toLocaleString("default", { month: "short" })
    .toUpperCase();

  const [year, setYear] = useState([]);
  const [yearName, setYearName] = useState("");
  const [division, setDivision] = useState([]);
  const [divisionName, setDivisionName] = useState([]);
  const [district, setDistrict] = useState([]);
  const [districtName, setDistrictName] = useState([]);
  const [monthName] = useState(prevMonthName);
  const [divisionList, setDivisionList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [reportResults, setReportResults] = useState([]);

  const token = useContext(MyContextProvider).token;
  const storedLanguage = localStorage.getItem("language");

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const decodedToken = jwtDecode(token);
  const userLevel = decodedToken.id.USER_TYPE;
  const userLevelLocation = decodedToken.id.USER_LEVEL_LOCATION;
  const userLevelLocationName = decodedToken.id.LOCATION_NAME;

  useEffect(() => {
    const initialize = async () => {
      showLoading(true);
      try {
        const role = await validateASIReportsRole(decodedToken);
        if (!role) {
          alert("You are not an authorized for ASI Report");
          return;
        }
        await fetchDivisionList();
        await getSurveyYears();
        if (userLevel === "HEAD QUARTER") {
          await getAllDistrictList();
        }
        if (userLevel === "DIVISION LEVEL") {
          setDivision(userLevelLocation);
          setDivisionName(userLevelLocationName);
          await fetchDistrictByDivision(userLevelLocation);
        }
      } catch (error) {
        console.error(error);
      } finally {
        showLoading(false);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDivisionList = async () => {
    try {
      const response = await axios.get(
        `${API_URL}api/getalldivisionallist`,
        config
      );
      const allDivision = response.data;
      if (allDivision.status) {
        setDivisionList(allDivision.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getSurveyYears = async () => {
    try {
      const response = await axios.get(
        `${API_URL}api/asi/surveyYearList`,
        config
      );
      const allYear = response.data;
      if (allYear.status) {
        setYear(allYear.data.surveyYearList.reverse());
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getAllDistrictList = async () => {
    try {
      const response = await axios.get(`${API_URL}api/getallDistrictList`, config);
      const allDistrict = response.data;
      if (allDistrict.status) {
        setDistrictList(allDistrict.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDistrictByDivision = async (divisionCode) => {
    if (!divisionCode) {
      setDistrictList([]);
      return;
    }
    showLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}api/geDistrictListByDivisionId`,
        { division_Id: divisionCode },
        config
      );
      const allDistricts = response.data;
      if (allDistricts.status) {
        setDistrictList(allDistricts.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      showLoading(false);
    }
  };

  const handleYearChange = (event) => {
    setYearName(event.target.value);
  };

  const handleDivisionChange = async (event) => {
    const selectedDivision = event.target.value;
    setDivision(selectedDivision);

    if (userLevel !== "DIVISION LEVEL") {
      const selectedText = event.target.options[event.target.selectedIndex].text;
      setDivisionName(selectedText === "ALL" ? "" : selectedText);
    }

    await fetchDistrictByDivision(selectedDivision);
  };

  const handleDistrictChange = (event) => {
    setDistrict(event.target.value);
    const selectedText = event.target.options[event.target.selectedIndex].text;
    setDistrictName(selectedText === "ALL" ? "" : selectedText);
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    if (!yearName) {
      toast.error("Please Select Survey Year", messages.COLOR_RED);
      return;
    }

    let divisionCode = division;
    let districtCode = district;

    if (Array.isArray(divisionCode) && divisionCode.length === 0) {
      divisionCode = "";
    }
    if (Array.isArray(districtCode) && districtCode.length === 0) {
      districtCode = "";
    }

    if (divisionCode === "00" || districtCode === "00") {
      toast.info("Data not found.", messages.COLOR_RED);
      setReportResults([]);
      return;
    }

    const post_data = {
      survey_year: yearName,
      division_code: divisionCode,
      district_code: districtCode,
    };

    try {
      showLoading(true);
      const response = await axios.post(
        `${API_URL}api/asi/reports/asiRejectedDSLReport`,
        post_data,
        config
      );
      const rejectedData = response.data;
      if (rejectedData.status) {
        // Map API response: divisionCode, divisionName, districtCode, districtName,
        // surveyYear, dslNo, rejectedBy, rejectionCount, rejectionDates, rejectionDatesText
        const rows = (rejectedData.data || []).map((item) => {
          // Use rejectionDatesText when available, else format rejectionDates array
          let allDates = item.rejectionDatesText || "";
          if (!allDates && item.rejectionDates && Array.isArray(item.rejectionDates) && item.rejectionDates.length > 0) {
            const formattedDates = item.rejectionDates.map((date) => {
              const dateParts = date.split("-");
              if (dateParts.length === 3) {
                return `${dateParts[0]}/${dateParts[1]}/${dateParts[2]}`;
              }
              return date;
            });
            allDates = formattedDates.join(", ");
          }

          return {
            divisionCode: item.divisionCode,
            divisionName: item.divisionName,
            districtCode: item.districtCode,
            districtName: item.districtName,
            surveyYear: item.surveyYear || "",
            rejectedDSL: item.dslNo != null ? [item.dslNo] : [],
            rejectedBy: item.rejectedBy || "",
            rejectedCount: item.rejectionCount ?? 0,
            rejectedDate: allDates,
          };
        });
        
        setReportResults(rows);
        // toast.success(
        //   rejectedData.message || "Rejected DSL data fetched successfully.",
        //   messages.COLOR_GREEN
        // );
      } else {
        setReportResults([]);
        toast.error(
          rejectedData.message || "No rejected DSL data found for the selected criteria.",
          messages.COLOR_RED
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(
        "An error occurred while fetching rejected DSL report.",
        messages.COLOR_RED
      );
    } finally {
      showLoading(false);
    }
  };



  const currentDateTimeShow = new Date().toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const generateFileName = (baseName) => {
    let excelFileName = baseName;
    if (typeof divisionName === "string" && divisionName !== "") {
      excelFileName = `${excelFileName}_${divisionName.replace(" ", "_")}`;
    }
    if (typeof districtName === "string" && districtName !== "") {
      excelFileName = `${excelFileName}_${districtName.replace(" ", "_")}`;
    }
    if (typeof yearName === "string" && yearName !== "") {
      excelFileName = `${excelFileName}_${yearName.replace(" ", "_")}`;
    }
    return excelFileName;
  };

  const handleGenerateExcel = () => {
    if (!yearName) {
      toast.error("Please Select Survey Year", messages.COLOR_RED);
      return;
    }

    if (!reportResults || !reportResults.length) {
      toast.error(
        "First, search the data, then export as excel",
        messages.COLOR_RED
      );
      return;
    }

    const excelFileName = generateFileName("ASI_RejectedDSLReport");
    const excelRowData = reportResults.map((item, index) => [
      index + 1,
      `${item.divisionName ?? ""} ${
        item.divisionCode ? `(${item.divisionCode})` : ""
      }`,
      `${item.districtName ?? ""} ${
        item.districtCode ? `(${item.districtCode})` : ""
      }`,
      item.surveyYear || "-",
      item.rejectedDSL && item.rejectedDSL.length
        ? item.rejectedDSL.join(", ")
        : "-",
      item.rejectedBy || "-",
      item.rejectedCount ?? 0,
      item.rejectedDate || "",
    ]);

    const excelObjectData = {
      fileName: excelFileName,
      reportType: excelFileName,
      fileType: "xls",
      sheetName: excelFileName,
      firstRowMergeCells: "A1:H1",
      firstRowExportDate: "ASI, Uttar Pradesh (Rejected DSL Report)",
      firstRowCellNumber: 8,
      secondRowMergeCells: "A2:H2",
      secondRowText: `Survey Year: ${yearName}     ${currentDateTimeShow}`,
      secondRowExportDate: changeDateFormat(new Date()),
      secondRowCellNumber: 8,
      headerEnglishColumns: [
        "S.No",
        "Division Name",
        "District Name",
        "Survey Year",
        "Rejected DSL",
        "Last Rejected By",
        "Rejected Count",
        "Rejected Date",
      ],
      excelRowData,
    };

    generateExcelASI(excelObjectData);
  };

  const handleGeneratePdf = () => {
    if (!yearName) {
      toast.error("Please Select Survey Year", messages.COLOR_RED);
      return;
    }

    if (!reportResults || !reportResults.length) {
      toast.error(
        "First, search the data, then export as pdf",
        messages.COLOR_RED
      );
      return;
    }

    const pdfData = reportResults.map((item, index) => [
      index + 1,
      `${item.divisionName ?? ""} ${
        item.divisionCode ? `(${item.divisionCode})` : ""
      }`,
      `${item.districtName ?? ""} ${
        item.districtCode ? `(${item.districtCode})` : ""
      }`,
      item.surveyYear || "-",
      item.rejectedDSL && item.rejectedDSL.length
        ? item.rejectedDSL.join(", ")
        : "-",
      item.rejectedBy || "-",
      item.rejectedCount ?? 0,
      item.rejectedDate || "",
    ]);

    const pdfObjectData = {
      heading1: "ASI, Uttar Pradesh",
      heading2: "(Rejected DSL Report)",
      heading3: `Survey Year: ${yearName}     ${currentDateTimeShow}`,
      headerEnglishColumns: [
        "S.No",
        "Division Name",
        "District Name",
        "Survey Year",
        "Rejected DSL",
        "Last Rejected By",
        "Rejected Count",
        "Rejected Date",
      ],
      pdfData,
      width: [50, 120, 120, 70, 100, 80, 90, 120],
      PDFFileName: generateFileName("ASI_RejectedDSLReport"),
    };

    GetASIRejectedDSLReportPDF(pdfObjectData);
  };

  return (
    <>
      <ToastContainer />
      <GlobalLoading />

      <div className="container-fluid py-3">
        <div className="card mx-3 mb-3">
          <div className="card-body pb-1">
            <div className="row align-items-center">
              <h4 className="page_heading">
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      storedLanguage === "hi"
                        ? asiTitle.hi.ASI_REJECTED_DSL_REPORT
                        : "ASI Rejected DSL Report",
                  }}
                />
              </h4>

              <div className="col-xl-2 col-lg-4 col-md-4 col-sm-4 col-6 mb-3">
                <label>
                  {storedLanguage === "hi"
                    ? asiTitle.hi.SERVEY_YEAR
                    : asiTitle.en.SERVEY_YEAR}
                </label>
                <select
                  value={yearName}
                  onChange={handleYearChange}
                  name="surveyYear"
                  className="form-select"
                >
                  <option value="">Select Year</option>
                  {year
                    .sort((a, b) => b - a)
                    .map((surveyYear, index) => (
                      <option key={index} value={surveyYear}>
                        {surveyYear}-
                        {((parseInt(surveyYear, 10) || 0) + 1)
                          .toString()
                          .slice(2)}
                      </option>
                    ))}
                </select>
              </div>

              {userLevel === "HEAD QUARTER" && (
                <>
                  <div className="col-xl-2 col-lg-4 col-md-4 col-sm-4 col-6 mb-3">
                    <label>
                      {storedLanguage === "hi"
                        ? asiTitle.hi.DIVISION
                        : asiTitle.en.DIVISION}
                    </label>

                    <select
                      value={division}
                      onChange={handleDivisionChange}
                      name="division"
                      className="form-select"
                    >
                      <option value="">ALL</option>
                      {divisionList.length > 0 &&
                        divisionList.map((ele) => (
                          <option
                            key={ele.DIVISION_CODE}
                            value={ele.DIVISION_CODE}
                          >
                            {ele.DIVISION_NAME}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="col-xl-2 col-lg-4 col-md-4 col-sm-4 col-6 mb-3">
                    <label>
                      {storedLanguage === "hi"
                        ? asiTitle.hi.DISTRICT
                        : asiTitle.en.DISTRICT}
                    </label>
                    <select
                      value={district}
                      onChange={handleDistrictChange}
                      name="district"
                      className="form-select"
                    >
                      <option value="">ALL</option>
                      {districtList.map((ele) => (
                        <option key={ele.DISTRICT_CODE} value={ele.DISTRICT_CODE}>
                          {ele.DISTRICT_NAME}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {userLevel === "DIVISION LEVEL" && (
                <div className="col-xl-2 col-lg-4 col-md-4 col-sm-4 col-6 mb-3">
                  <label>
                    {storedLanguage === "hi"
                      ? asiTitle.hi.DISTRICT
                      : asiTitle.en.DISTRICT}
                  </label>
                  <select
                    value={district}
                    onChange={handleDistrictChange}
                    name="district"
                    className="form-select"
                  >
                    <option value="">ALL</option>
                    {districtList.map((ele) => (
                      <option key={ele.DISTRICT_CODE} value={ele.DISTRICT_CODE}>
                        {ele.DISTRICT_NAME}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-xl-2 col-lg-12 mb-3">
                <label className="w-100 d-none d-sm-block">&nbsp;</label>
                <button onClick={handleSearch} className="btn btn_orange">
                  Search
                </button>
              </div>
              <div className="col-xl-4 col-lg-12 mb-3 text-end">
                <label className="w-100 d-none d-sm-block">&nbsp;</label>
                <button
                  type="button"
                  className="btn btn_green px-3 ms-auto"
                  onClick={handleGenerateExcel}
                >
                  <img
                    src="assets/images/excel.svg"
                    alt="download Excel"
                    className="me-2"
                  />
                  Excel
                </button>
                <button
                  type="button"
                  className="btn btn_green ms-1 px-3"
                  onClick={handleGeneratePdf}
                >
                  <img
                    src="assets/images/pdf.svg"
                    alt="download Pdf"
                    className="me-2"
                  />
                  PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {reportResults && reportResults.length > 0 && (
          <div className="card mx-3 mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 fw-semibold">
                  {storedLanguage === "hi"
                    ? "अस्वीकृत DSL विवरण"
                    : "Rejected DSL Details"}
                </h6>
               
              </div>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Division Name</th>
                      <th>District Name</th>
                      <th>Survey Year</th>
                      <th>Rejected DSL</th>
                      <th>Last Rejected By</th>
                      <th>Rejected Count</th>
                      <th>Rejected Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportResults.map((item, index) => (
                      <tr key={`${item.divisionCode}-${item.districtCode}-${(item.rejectedDSL && item.rejectedDSL[0]) ?? index}-${index}`}>
                        <td>{index + 1}</td>
                        <td>
                          {item?.divisionName
                            ? `${item.divisionName} (${item.divisionCode})`
                            : ""}
                        </td>
                        <td>
                          {item?.districtName
                            ? `${item.districtName} (${item.districtCode})`
                            : ""}
                        </td>
                        <td>{item?.surveyYear || "-"}</td>
                        <td>
                          {item?.rejectedDSL && item.rejectedDSL.length > 0
                            ? item.rejectedDSL.join(", ")
                            : "-"}
                        </td>
                        <td>{item?.rejectedBy || "-"}</td>
                        <td>
                          {item?.rejectedCount ?? 0}
                        </td>
                        <td>
                          {item?.rejectedDate ? item.rejectedDate : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default GetASIRejectedDSLReport;

